import { useEffect, useRef, useState } from "react";
import { ApiError, type Location, type OrderPlan, type PlannedOrder, type Product, type SkuChoice, type TikTokPayout, api, formatMoney } from "./api";
import { SelectField } from "./fields";
import { Alert, Badge, Empty, Modal, Table, Th, TwoLine, Working } from "./ui";
import { productCode } from "./productCode";
import { productName } from "./productName";

/**
 * TikTok Shop orders, from the Seller Center export (quotation E3).
 *
 * The file is read and shown back first: every order with what it will become, and
 * every listing the system could not match to a product, with a picker. Nothing is
 * written until the person has seen that list and says to go ahead. A listing mapped
 * here is remembered, so the next month's file asks about it no more.
 */
export function TikTokImport({
  locations,
  accounts,
  onClose,
  onDone,
}: {
  readonly locations: readonly Location[];
  readonly accounts: readonly { id: string; name: string }[];
  readonly onClose: () => void;
  readonly onDone: (message: string) => void;
}): React.ReactElement {
  const [file, setFile] = useState<{ name: string; content: string } | undefined>();
  /** TikTok's payout file, read beside the orders so each bank credit is explained (D, 2 November 2026). */
  const [payoutFile, setPayoutFile] = useState<{ name: string; content: string } | undefined>();
  /** Listings given a product by the suggestion, and how: shown so the person checks them. */
  const [suggested, setSuggested] = useState<Map<string, "ai" | "words">>(new Map());
  const [suggestNote, setSuggestNote] = useState<string | undefined>();
  const [plan, setPlan] = useState<OrderPlan | undefined>();
  const [products, setProducts] = useState<Product[]>([]);
  const [accountId, setAccountId] = useState("");
  const [locationId, setLocationId] = useState("");
  const [choices, setChoices] = useState<SkuChoice[]>([]);
  const [reverseCancelled, setReverseCancelled] = useState(true);
  /** Orders paid before this day are left out. Undefined until the first reading gives the books' first day. */
  const [fromDay, setFromDay] = useState<string | null | undefined>();
  const [problem, setProblem] = useState<string | undefined>();
  const [busy, setBusy] = useState<"reading" | "importing" | undefined>();
  const picker = useRef<HTMLInputElement>(null);
  const payoutPicker = useRef<HTMLInputElement>(null);

  useEffect(() => {
    void api.products().then((result) => setProducts(result.products.filter((product) => product.isActive)));
  }, []);

  async function preview(
    chosen: { name: string; content: string },
    withChoices: SkuChoice[],
    from: string | null | undefined = fromDay,
    payouts: { name: string; content: string } | undefined = payoutFile,
    suggest = false,
  ): Promise<void> {
    setBusy("reading");
    setProblem(undefined);
    try {
      const result = await api.previewTikTokOrders({
        fileName: chosen.name,
        content: chosen.content,
        choices: withChoices,
        ...(from === undefined ? {} : { fromDay: from }),
        payoutFile: payouts ? { fileName: payouts.name, content: payouts.content } : null,
      });
      setPlan(result.plan);
      setFromDay(result.plan.fromDay);
      if (!accountId) setAccountId(result.plan.settings.accountId ?? accounts[0]?.id ?? "");
      // The first reading of a file: a product suggested for every listing it can be told for,
      // already picked for the person to check, as an invoice's lines are (D, 2 November 2026).
      const open = result.plan.unmapped.filter((sku) => !sku.chosenProductId);
      if (suggest && open.length > 0) {
        const { suggestions, usedAi, aiProblem } = await api.suggestTikTokListings(
          open.map((sku) => ({ key: listingKey(sku), productName: sku.productName, variation: sku.variation })),
        );
        if (suggestions.length > 0) {
          const next = [...withChoices];
          const how = new Map<string, "ai" | "words">();
          for (const suggestion of suggestions) {
            const sku = open.find((entry) => listingKey(entry) === suggestion.key);
            if (!sku) continue;
            next.push({ skuId: sku.skuId, sellerSku: sku.sellerSku, productName: sku.productName, variation: sku.variation, productId: suggestion.productId, units: sku.units });
            how.set(suggestion.key, suggestion.by);
          }
          setSuggested(how);
          setChoices(next);
          const again = await api.previewTikTokOrders({
            fileName: chosen.name,
            content: chosen.content,
            choices: next,
            ...(from === undefined ? {} : { fromDay: from }),
            payoutFile: payouts ? { fileName: payouts.name, content: payouts.content } : null,
          });
          setPlan(again.plan);
        }
        setSuggestNote(
          `${suggestions.length} of ${open.length} ${open.length === 1 ? "listing" : "listings"} matched ${usedAi ? "by AI" : "by their words"}. Check each before importing.` +
            (aiProblem ? ` The AI could not be used: ${aiProblem}.` : ""),
        );
      }
    } catch (error) {
      setProblem(error instanceof ApiError ? error.message : "The file could not be read");
      setPlan(undefined);
    } finally {
      setBusy(undefined);
    }
  }

  async function choose(chosen: File | undefined): Promise<void> {
    if (!chosen) return;
    setPlan(undefined);
    setChoices([]);
    setSuggested(new Map());
    setSuggestNote(undefined);
    setFromDay(undefined);
    try {
      const content = await base64(chosen);
      const picked = { name: chosen.name, content };
      setFile(picked);
      await preview(picked, [], undefined, payoutFile, true);
    } catch {
      setProblem("The file could not be read");
    } finally {
      if (picker.current) picker.current.value = "";
    }
  }

  /** The payout file: read with the orders already chosen, or kept until they are. */
  async function choosePayouts(chosen: File | undefined): Promise<void> {
    if (!chosen) return;
    try {
      const picked = { name: chosen.name, content: await base64(chosen) };
      setPayoutFile(picked);
      if (file) await preview(file, choices, fromDay, picked);
    } catch {
      setProblem("The payout file could not be read");
    } finally {
      if (payoutPicker.current) payoutPicker.current.value = "";
    }
  }

  /** A product picked for a listing: the plan is redone so the orders it blocked move to ready. */
  async function map(sku: OrderPlan["unmapped"][number], productId: string, units: number = sku.units): Promise<void> {
    if (!file) return;
    const next = choices.filter((choice) => !(choice.skuId === sku.skuId && choice.productName === sku.productName && choice.variation === sku.variation));
    if (productId) next.push({ skuId: sku.skuId, sellerSku: sku.sellerSku, productName: sku.productName, variation: sku.variation, productId, units });
    setChoices(next);
    // Once the person has touched it, it is theirs, not a suggestion.
    setSuggested((before) => {
      const after = new Map(before);
      after.delete(listingKey(sku));
      return after;
    });
    await preview(file, next);
  }

  async function go(): Promise<void> {
    if (!file || !plan) return;
    setBusy("importing");
    setProblem(undefined);
    try {
      const { result } = await api.importTikTokOrders({
        fileName: file.name,
        content: file.content,
        accountId,
        locationId: locationId || null,
        choices,
        reverseCancelled,
        fromDay: fromDay ?? null,
        payoutFile: payoutFile ? { fileName: payoutFile.name, content: payoutFile.content } : null,
      });
      onDone(
        `${result.sales} ${result.sales === 1 ? "order" : "orders"} imported as sales, ${formatMoney(result.totalCents)}, under device code ${result.deviceCode}` +
          (result.reversed > 0 ? `, ${result.reversed} reversed` : "") +
          (result.leftOut > 0 ? `, ${result.leftOut} excluded` : "") +
          (payoutFile
            ? `. ${result.payoutsMatched} ${result.payoutsMatched === 1 ? "payout" : "payouts"} matched to the bank` +
              (result.payoutsWaiting > 0 ? `, ${result.payoutsWaiting} waiting for their bank line` : "")
            : "") +
          (result.notes.length > 0 ? `. ${result.notes.join(" ")}` : "."),
      );
    } catch (error) {
      setProblem(error instanceof ApiError ? error.message : "Could not save");
    } finally {
      setBusy(undefined);
    }
  }

  const ready = plan?.totals.ready ?? 0;
  const toReverse = plan?.totals.toReverse ?? 0;
  const payoutsToKeep = (plan?.payouts ?? []).filter((payout) => !payout.problem && !payout.bankLine).length;
  const canGo = plan !== undefined && accountId !== "" && (ready > 0 || (toReverse > 0 && reverseCancelled) || payoutsToKeep > 0);

  return (
    <Modal
      open
      size="xl"
      title="Import TikTok orders"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ti-btn ti-btn-light" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="ti-btn ti-btn-primary-full" disabled={busy !== undefined || !canGo} onClick={() => void go()}>
            {busy === "importing" ? (
              <Working label="Importing" />
            ) : ready > 0 ? (
              `Import ${ready} ${ready === 1 ? "order" : "orders"}`
            ) : toReverse > 0 && reverseCancelled ? (
              `Reverse ${toReverse} ${toReverse === 1 ? "order" : "orders"}`
            ) : payoutsToKeep > 0 ? (
              `Match ${payoutsToKeep} ${payoutsToKeep === 1 ? "payout" : "payouts"}`
            ) : (
              "Nothing to import"
            )}
          </button>
        </>
      }
    >
      {problem ? <Alert tone="danger">{problem}</Alert> : null}

      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12">
          <label htmlFor="tiktok-file" className="form-label">
            1. Orders file
          </label>
          <div className="flex flex-wrap items-center gap-3">
            <button type="button" className="ti-btn ti-btn-light !mb-0" disabled={busy !== undefined} onClick={() => picker.current?.click()}>
              {busy === "reading" ? <Working label="Reading" /> : <><i className="ri-file-excel-2-line" /> Select file</>}
            </button>
            <span className="text-[#8c9097] dark:text-white/50">
              {file ? file.name : "Seller Center, Orders, Export, All orders (CSV)"}
            </span>
          </div>
          <input
            ref={picker}
            id="tiktok-file"
            type="file"
            accept=".xlsx,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="hidden"
            onChange={(changed) => void choose(changed.target.files?.[0])}
          />
        </div>
        <div className="col-span-12">
          <label htmlFor="tiktok-payouts" className="form-label">
            2. Payout file
          </label>
          <div className="flex flex-wrap items-center gap-3">
            <button type="button" className="ti-btn ti-btn-light !mb-0" disabled={busy !== undefined} onClick={() => payoutPicker.current?.click()}>
              <i className="ri-bank-line" /> Select file
            </button>
            <span className="text-[#8c9097] dark:text-white/50">
              {payoutFile ? payoutFile.name : "Seller Center, Finance, Payouts, Export (.xlsx). Matches each TikTok payout to its OCBC credit"}
            </span>
          </div>
          <input
            ref={payoutPicker}
            id="tiktok-payouts"
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="hidden"
            onChange={(changed) => void choosePayouts(changed.target.files?.[0])}
          />
        </div>

        <div className="sm:col-span-6 col-span-12">
          <label htmlFor="tiktok-account" className="form-label">
            TikTok payout account
          </label>
          <SelectField
            id="tiktok-account"
            value={accountId}
            placeholder="Select"
            options={accounts.map((account) => ({ value: account.id, label: account.name }))}
            onChange={setAccountId}
          />
        </div>
        <div className="sm:col-span-6 col-span-12">
          <label htmlFor="tiktok-place" className="form-label">
            Dispatch location
          </label>
          <SelectField
            id="tiktok-place"
            value={locationId}
            placeholder="Any location with inventory"
            options={locations.map((location) => ({ value: location.id, label: location.name }))}
            onChange={setLocationId}
          />
        </div>
        {plan ? (
          <div className="sm:col-span-6 col-span-12">
            <label htmlFor="tiktok-from" className="form-label">
              Orders paid from
            </label>
            <input
              id="tiktok-from"
              type="date"
              className="form-control"
              value={fromDay ?? ""}
              disabled={busy !== undefined}
              onChange={(changed) => {
                const day = changed.target.value || null;
                setFromDay(day);
                if (file) void preview(file, choices, day);
              }}
            />
            <p className="text-[0.75rem] text-[#8c9097] dark:text-white/50 mt-1 mb-0">
              Empty takes every order. A day here leaves out orders paid before it.
            </p>
          </div>
        ) : null}
      </div>

      {plan ? (
        <div className="mt-4">
          {plan.importedBefore ? (
            <Alert tone="info">This file was imported on {plan.importedBefore.on} by {plan.importedBefore.by}. Orders already imported are not changed.</Alert>
          ) : null}
          {plan.warnings.length > 0 ? (
            <Alert tone="warning">
              <ul className="list-disc ms-4 mb-0">
                {plan.warnings.map((warning) => (
                  <li key={warning}>{warning}</li>
                ))}
              </ul>
            </Alert>
          ) : null}

          <div className="flex flex-wrap items-center gap-3 mb-3">
            <Badge tone="success">{plan.totals.ready} ready to import</Badge>
            {plan.totals.alreadyIn > 0 ? <Badge tone="light">{plan.totals.alreadyIn} already imported</Badge> : null}
            {plan.totals.toReverse > 0 ? <Badge tone="warning">{plan.totals.toReverse} since cancelled</Badge> : null}
            {plan.totals.needProduct > 0 ? <Badge tone="danger">{plan.totals.needProduct} need a product match</Badge> : null}
            {plan.totals.leftOut > 0 ? <Badge tone="danger">{plan.totals.leftOut} excluded</Badge> : null}
            <span className="text-[#8c9097] dark:text-white/50">{formatMoney(plan.totals.readyCents)} to import</span>
            <div className="flex-1" />
            {plan.settings.deviceCode ? (
              <span className="text-[0.75rem] text-[#8c9097] dark:text-white/50">Sales are recorded under device code {plan.settings.deviceCode}</span>
            ) : null}
          </div>

          {plan.totals.toReverse > 0 ? (
            <label className="flex items-center gap-2 text-[0.813rem] mb-3">
              <input type="checkbox" className="form-check-input" checked={reverseCancelled} onChange={(changed) => setReverseCancelled(changed.target.checked)} />
              Reverse the {plan.totals.toReverse} {plan.totals.toReverse === 1 ? "order" : "orders"} TikTok now shows as cancelled. The inventory is restored and the sale is marked as reversed.
            </label>
          ) : null}

          {plan.payouts ? <Payouts payouts={plan.payouts} warnings={plan.payoutWarnings} /> : null}

          {plan.unmapped.length > 0 ? (
            <div className="box !shadow-none border border-defaultborder dark:border-defaultborder/10 mb-3">
              <div className="box-header">
                <div className="box-title">Match listings to products</div>
              </div>
              <div className="box-body">
                <p className="text-[0.75rem] text-[#8c9097] dark:text-white/50 mb-3">
                  Select the product for each listing, and how many of it one listing sells: "10 Packs" takes 10 packs from inventory. The match is saved for future imports. Orders for an unmatched listing are excluded until it is matched.
                </p>
                {suggestNote ? <Alert tone="info">{suggestNote}</Alert> : null}
                {plan.unmapped.map((sku) => {
                  const key = `${sku.skuId ?? ""}|${sku.productName}|${sku.variation ?? ""}`;
                  const current = choices.find((choice) => choice.skuId === sku.skuId && choice.productName === sku.productName && choice.variation === sku.variation);
                  const fieldId = `sku-${key.replace(/[^A-Za-z0-9]/g, "-")}`;
                  return (
                    <div key={key} className="grid grid-cols-12 gap-3 items-center mb-3">
                      <div className="sm:col-span-5 col-span-12">
                        <TwoLine
                          main={sku.productName}
                          sub={[sku.variation, sku.sellerSku ? `SKU ${sku.sellerSku}` : null, `${sku.orders} ${sku.orders === 1 ? "order" : "orders"}`].filter(Boolean).join(" · ")}
                        />
                        {current && products.find((product) => product.id === current.productId)?.tracksStock === false ? (
                          <p className="text-warning text-[0.75rem] mb-1">
                            This product&apos;s quantity is not tracked, so these sales take nothing from inventory. Choose the counted product, or turn on its quantity tracking.
                          </p>
                        ) : null}
                        {suggested.has(listingKey(sku)) && current ? (
                          <Badge tone="info">{suggested.get(listingKey(sku)) === "ai" ? "Suggested by AI, check it" : "Suggested, check it"}</Badge>
                        ) : null}
                      </div>
                      <div className="sm:col-span-5 col-span-8">
                        <SelectField
                          id={fieldId}
                          value={current?.productId ?? ""}
                          placeholder="Exclude"
                          options={products.map((product) => ({
                            value: product.id,
                            label: `${productCode(product)} ${productName(product)}${product.tracksStock ? "" : " (quantity not tracked)"}`,
                          }))}
                          onChange={(productId) => void map(sku, productId)}
                        />
                      </div>
                      <div className="sm:col-span-2 col-span-4">
                        <UnitsField
                          id={`${fieldId}-units`}
                          units={sku.units}
                          disabled={!current || busy !== undefined}
                          onChange={(units) => (current ? void map(sku, current.productId, units) : undefined)}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}

          {plan.orders.length === 0 ? (
            <Empty icon="ri-file-list-3-line">No orders were found below the header row.</Empty>
          ) : (
            <div className="max-h-[22rem] overflow-y-auto">
              <Table
                head={
                  <>
                    <Th>Order</Th>
                    <Th>Paid</Th>
                    <Th>Items</Th>
                    <Th end>Total</Th>
                    <Th>Import result</Th>
                  </>
                }
              >
                {plan.orders.map((order) => (
                  <tr key={order.orderId} className="border-b border-defaultborder">
                    <td>
                      <TwoLine main={order.orderId} sub={order.buyer ?? undefined} />
                    </td>
                    <td className="text-[#8c9097] dark:text-white/50">{order.paidAt ? paidWhen(order.paidAt) : "-"}</td>
                    <td className="max-w-[18rem] !whitespace-normal">
                      {order.lines
                        .map((line) =>
                          line.productLabel
                            ? `${line.quantity * line.units} × ${line.productLabel}`
                            : `${line.quantity} × ${line.productName}${line.variation ? `, ${line.variation}` : ""}`,
                        )
                        .join("; ")}
                    </td>
                    <td className="!text-end">{formatMoney(order.totalCents)}</td>
                    <td>
                      <OutcomeBadge order={order} />
                      {order.reason ? <p className="text-[0.75rem] text-danger mb-0 !whitespace-normal max-w-[16rem]">{order.reason}</p> : null}
                    </td>
                  </tr>
                ))}
              </Table>
            </div>
          )}
        </div>
      ) : null}
    </Modal>
  );
}

/** How many of the product one listing takes, written in when the field is left. */
function UnitsField({
  id,
  units,
  disabled,
  onChange,
}: {
  readonly id: string;
  readonly units: number;
  readonly disabled: boolean;
  readonly onChange: (units: number) => void;
}): React.ReactElement {
  const [text, setText] = useState(String(units));
  useEffect(() => setText(String(units)), [units]);
  const commit = (): void => {
    const value = Number(text);
    if (Number.isInteger(value) && value >= 1 && value <= 1000) {
      if (value !== units) onChange(value);
    } else {
      setText(String(units));
    }
  };
  return (
    <div className="input-group" title="How many of the product one of this listing takes from inventory">
      <span className="input-group-text !px-2 text-[0.75rem]">×</span>
      <input
        id={id}
        type="number"
        min={1}
        max={1000}
        inputMode="numeric"
        aria-label="Units per listing"
        className="form-control !px-2 min-w-[3.5rem]"
        value={text}
        disabled={disabled}
        onChange={(changed) => setText(changed.target.value)}
        onBlur={commit}
        onKeyDown={(pressed) => {
          if (pressed.key === "Enter") commit();
        }}
      />
    </div>
  );
}

/** How a listing is told apart on this screen: the same key the suggestions come back with. */
function listingKey(sku: { skuId: string | null; productName: string; variation: string | null }): string {
  return `${sku.skuId ?? ""}|${sku.productName}|${sku.variation ?? ""}`;
}

/**
 * The payouts in the payout file, as D laid them out (2 November 2026): the orders' revenue,
 * less TikTok's fees, is what reached the bank, and each payout is matched to that credit.
 */
function Payouts({ payouts, warnings }: { readonly payouts: readonly TikTokPayout[]; readonly warnings: readonly string[] }): React.ReactElement {
  const total = (pick: (payout: TikTokPayout) => number): number => payouts.reduce((sum, payout) => sum + pick(payout), 0);
  const fee = (cents: number): string => formatMoney(-cents);
  return (
    <div className="box !shadow-none border border-defaultborder dark:border-defaultborder/10 mb-3">
      <div className="box-header">
        <div className="box-title">TikTok payouts</div>
      </div>
      <div className="box-body">
        <p className="text-[0.75rem] text-[#8c9097] dark:text-white/50 mb-3">
          Revenue less TikTok&apos;s fees is the amount paid into the bank. On import each payout is matched to its bank credit of the same amount; one whose
          bank line is not imported yet is matched when the statement is.
        </p>
        {warnings.length > 0 ? (
          <Alert tone="warning">
            <ul className="list-disc ms-4 mb-0">
              {warnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          </Alert>
        ) : null}
        <div className="overflow-x-auto">
          <Table
            head={
              <>
                <Th>Paid out</Th>
                <Th end>Revenue</Th>
                <Th end>Transaction fee</Th>
                <Th end>Commission</Th>
                <Th end>Seller shipping</Th>
                <Th end>Other fees</Th>
                <Th end>Total fees</Th>
                <Th end>Bank deposit</Th>
                <Th end>Orders</Th>
                <Th>Bank</Th>
              </>
            }
          >
            {payouts.map((payout) => (
              <tr key={payout.referenceId} className="border-b border-defaultborder">
                <td className="whitespace-nowrap">{shortDay(payout.paidOn)}</td>
                <td className="!text-end">{formatMoney(payout.revenueCents)}</td>
                <td className="!text-end">{fee(payout.transactionFeeCents)}</td>
                <td className="!text-end">{fee(payout.commissionCents)}</td>
                <td className="!text-end">{fee(payout.shippingCents)}</td>
                <td className="!text-end">{fee(payout.otherFeesCents)}</td>
                <td className="!text-end font-semibold">{fee(payout.feesCents)}</td>
                <td className="!text-end font-semibold">{formatMoney(payout.amountCents)}</td>
                <td className="!text-end">
                  {payout.orderCount}
                  {payout.ordersInBooks < payout.orderCount ? (
                    <div className="text-[0.6875rem] text-[#8c9097] dark:text-white/50 whitespace-nowrap">{payout.ordersInBooks} in the books</div>
                  ) : null}
                </td>
                <td className="whitespace-nowrap">
                  {payout.problem ? (
                    <span className="text-danger text-[0.75rem] !whitespace-normal">{payout.problem}</span>
                  ) : payout.bankLine ? (
                    <Badge tone="success">Matched, {shortDay(payout.bankLine.date)}</Badge>
                  ) : (
                    <Badge tone="light">To match</Badge>
                  )}
                </td>
              </tr>
            ))}
            <tr className="font-semibold">
              <td>Total</td>
              <td className="!text-end">{formatMoney(total((payout) => payout.revenueCents))}</td>
              <td className="!text-end">{fee(total((payout) => payout.transactionFeeCents))}</td>
              <td className="!text-end">{fee(total((payout) => payout.commissionCents))}</td>
              <td className="!text-end">{fee(total((payout) => payout.shippingCents))}</td>
              <td className="!text-end">{fee(total((payout) => payout.otherFeesCents))}</td>
              <td className="!text-end">{fee(total((payout) => payout.feesCents))}</td>
              <td className="!text-end">{formatMoney(total((payout) => payout.amountCents))}</td>
              <td className="!text-end">{total((payout) => payout.orderCount)}</td>
              <td />
            </tr>
          </Table>
        </div>
      </div>
    </div>
  );
}

/** 23 Jul 2026, as a payout's day reads. */
function shortDay(day: string): string {
  return new Date(`${day}T12:00:00+08:00`).toLocaleDateString("en-SG", { day: "numeric", month: "short", year: "numeric" });
}

function OutcomeBadge({ order }: { readonly order: PlannedOrder }): React.ReactElement {
  if (order.outcome === "new") return <Badge tone="success">New sale</Badge>;
  if (order.outcome === "already_in") return <Badge tone="light">Already imported as {order.reference}</Badge>;
  if (order.outcome === "to_reverse") return <Badge tone="warning">Cancelled - to be reversed</Badge>;
  if (order.outcome === "refunded_after") return <Badge tone="warning">Refunded after import</Badge>;
  if (order.outcome === "needs_product") return <Badge tone="danger">Product match required</Badge>;
  return <Badge tone="danger">Excluded</Badge>;
}

/** When it was paid, on a Singapore clock. */
function paidWhen(iso: string): string {
  return new Intl.DateTimeFormat("en-SG", { timeZone: "Asia/Singapore", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }).format(new Date(iso));
}

/** The file as base64, which is how it travels: a spreadsheet is not text. */
async function base64(file: File): Promise<string> {
  const buffer = new Uint8Array(await file.arrayBuffer());
  let binary = "";
  for (let at = 0; at < buffer.length; at += 0x8000) {
    binary += String.fromCharCode(...buffer.subarray(at, at + 0x8000));
  }
  return btoa(binary);
}
